// Dati finti del mockup "Ricarica veicoli".
// Le forme ricalcano le risposte del contratto API
// (docs/ricarica-veicoli-contratto-api.md): le pagine vere riuseranno questi tipi.

import { giornoRoma } from '../../../../utils/dateRoma';

// ── Tipi ─────────────────────────────────────────────────────────────────────

export type StatoConnettore =
  | 'Available' | 'Preparing' | 'Charging' | 'SuspendedEV' | 'SuspendedEVSE'
  | 'Finishing' | 'Reserved' | 'Unavailable' | 'Faulted';

export interface SessioneInCorso {
  id: number;
  transactionId: number;
  dipendente: string | null;
  inizioAt: string;
  energiaWh: number;
}

export interface Connettore {
  numero: number;
  stato: StatoConnettore;
  codiceErrore: string | null;
  sessioneInCorso: SessioneInCorso | null;
}

export interface Colonnina {
  id: number;
  societaId: number;
  societaNome: string;
  nome: string;
  codiceOcpp: string;
  sede: { id: number; nome: string } | null;
  marca: string | null;
  modello: string | null;
  seriale: string | null;
  firmware: string | null;
  collegata: boolean;
  ultimoContattoAt: string | null;
  attiva: boolean;
  passwordImpostata: boolean;
  connettori: Connettore[];
}

export interface StatoGateway {
  raggiungibile: boolean;
  ultimoContattoAt: string | null;
}

export type TipoComando =
  | 'AVVIO_REMOTO' | 'STOP_REMOTO' | 'RESET' | 'SBLOCCO'
  | 'DISPONIBILITA' | 'RICHIESTA_STATO' | 'LEGGI_CONFIGURAZIONE';

export type StatoComando = 'IN_ATTESA' | 'INVIATO' | 'ACCETTATO' | 'RIFIUTATO' | 'ERRORE' | 'SCADUTO';

export interface Comando {
  id: number;
  tipo: TipoComando;
  parametri: Record<string, string | number | boolean> | null;
  stato: StatoComando;
  risposta: Record<string, unknown> | null;
  messaggio: string | null;
  richiestoDa: string;
  createdAt: string;
  esitoAt: string | null;
}

export interface Tariffa {
  id: number;
  prezzoKwh: number;
  validaDal: string;
  creatoDa: string;
}

export interface Badge {
  id: number;
  codice: string;
  descrizione: string | null;
  dipendente: { id: number; nome: string; cognome: string };
  validoDal: string;
  validoAl: string | null;
  attivo: boolean;
  ultimoUtilizzoAt: string | null;
}

export interface BadgeSconosciuto {
  codiceBadge: string;
  tentativi: number;
  ultimoTentativoAt: string;
  colonnina: { id: number; nome: string; sede: string | null };
}

export type StatoSessione = 'IN_CORSO' | 'CONCLUSA' | 'ANOMALA';

export interface Sessione {
  id: number;
  transactionId: number;
  colonnina: { id: number; nome: string; societaNome: string; sede: string | null };
  connettore: number;
  dipendente: { id: number; nome: string; cognome: string; societaNome: string } | null;
  codiceBadge: string;
  inizioAt: string;
  fineAt: string | null;
  energiaWh: number | null;
  prezzoKwh: number | null;
  importo: number | null;
  stato: StatoSessione;
  motivoAnomalia: string | null;
  tardiva: boolean;
  competenza: { anno: number; mese: number } | null;
}

export interface RigaRiaddebito {
  dipendente: { id: number; nome: string; cognome: string; codiceFiscale: string };
  sessioni: number;
  energiaWh: number;
  importo: number;
  tardive: number;
}

export interface Riaddebito {
  periodo: { stato: 'APERTO' | 'CHIUSO'; chiusoAt: string | null; chiusoDa: string | null };
  righe: RigaRiaddebito[];
  totali: { sessioni: number; energiaWh: number; importo: number };
  avvisi: { anomale: number; inCorso: number };
}

export interface DipendenteOpzione { id: number; nome: string; cognome: string; }
export interface VoceConfigurazione { key: string; value?: string; readonly: boolean; }
export interface SedeOpzione { id: number; nome: string; }
export interface SocietaOpzione { id: number; nome: string; }

// ── Etichette ────────────────────────────────────────────────────────────────

export const STATO_CONNETTORE: Record<StatoConnettore, { label: string; badge: string }> = {
  Available: { label: 'Libera', badge: 'bop-badge--success' },
  Preparing: { label: 'In preparazione', badge: 'bop-badge--info' },
  Charging: { label: 'In carica', badge: 'bop-badge--info' },
  SuspendedEV: { label: "In pausa dall'auto", badge: 'bop-badge--warning' },
  SuspendedEVSE: { label: 'In pausa dalla colonnina', badge: 'bop-badge--warning' },
  Finishing: { label: 'In chiusura', badge: 'bop-badge--info' },
  Reserved: { label: 'Riservata', badge: 'bop-badge--neutral' },
  Unavailable: { label: 'Fuori servizio', badge: 'bop-badge--neutral' },
  Faulted: { label: 'Guasta', badge: 'bop-badge--danger' },
};

export const TIPO_COMANDO: Record<TipoComando, string> = {
  AVVIO_REMOTO: 'Avvio da remoto',
  STOP_REMOTO: 'Stop da remoto',
  RESET: 'Riavvio',
  SBLOCCO: 'Sblocco presa',
  DISPONIBILITA: 'Disponibilità',
  RICHIESTA_STATO: 'Aggiornamento stato',
  LEGGI_CONFIGURAZIONE: 'Lettura configurazione',
};

export const STATO_COMANDO: Record<StatoComando, { label: string; badge: string }> = {
  IN_ATTESA: { label: 'In attesa', badge: 'bop-badge--neutral' },
  INVIATO: { label: 'Inviato', badge: 'bop-badge--info' },
  ACCETTATO: { label: 'Eseguito', badge: 'bop-badge--success' },
  RIFIUTATO: { label: 'Rifiutato', badge: 'bop-badge--warning' },
  ERRORE: { label: 'Errore', badge: 'bop-badge--danger' },
  SCADUTO: { label: 'Scaduto', badge: 'bop-badge--neutral' },
};

export const STATO_SESSIONE: Record<StatoSessione, { label: string; badge: string }> = {
  IN_CORSO: { label: 'In corso', badge: 'bop-badge--info' },
  CONCLUSA: { label: 'Conclusa', badge: 'bop-badge--success' },
  ANOMALA: { label: 'Anomala', badge: 'bop-badge--danger' },
};

export const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

// ── Funzioni ─────────────────────────────────────────────────────────────────

const FMT_DATA_ORA = new Intl.DateTimeFormat('it-IT', {
  timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const FMT_DATA = new Intl.DateTimeFormat('it-IT', {
  timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric',
});

export function formatDataOra(iso: string | null): string {
  return iso ? FMT_DATA_ORA.format(new Date(iso)) : '—';
}

export function formatData(iso: string | null): string {
  return iso ? FMT_DATA.format(new Date(iso)) : '—';
}

export function whInKwh(wh: number): number {
  return wh / 1000;
}

// Solo per il mockup: il calcolo vero sarà in backend/src/services/ricarica/calcoli.js
export function arrotonda2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// Mese 'AAAA-MM' a cui il mockup assegna una sessione: competenza se c'è,
// altrimenti mese di inizio in ora italiana
export function meseDi(s: Sessione): string {
  if (s.competenza) return `${s.competenza.anno}-${String(s.competenza.mese).padStart(2, '0')}`;
  return giornoRoma(s.inizioAt).slice(0, 7);
}

export function etichettaMese(m: string): string {
  const [anno, mese] = m.split('-');
  return `${MESI[Number(mese) - 1]} ${anno}`;
}

// ── Dati ─────────────────────────────────────────────────────────────────────

// Data fissa del mockup, così gli stati (tariffa in vigore, badge scaduti) non cambiano
export const OGGI = '2026-10-25';
export const DOMANI = '2026-10-26';
export const MESI_DISPONIBILI = ['2026-10', '2026-09'];

export const SOCIETA: SocietaOpzione[] = [
  { id: 1, nome: 'Pianinvest' },
  { id: 2, nome: 'Gruppo Bonacina' },
];

export const SEDI: SedeOpzione[] = [
  { id: 1, nome: 'Sede centrale' },
  { id: 2, nome: 'Magazzino nord' },
  { id: 3, nome: 'Showroom' },
];

// Tariffa in vigore oggi per società della colonnina (serve alla conferma delle anomale)
export const PREZZO_IN_VIGORE: Record<string, number> = {
  Pianinvest: 0.25,
  'Gruppo Bonacina': 0.3,
};

export const GATEWAY: StatoGateway = { raggiungibile: true, ultimoContattoAt: '2026-10-25T12:45:00Z' };

export const COLONNINE: Colonnina[] = [
  {
    id: 1, societaId: 1, societaNome: 'Pianinvest', nome: 'Parcheggio ingresso 1', codiceOcpp: 'PIA-SEDE-01',
    sede: { id: 1, nome: 'Sede centrale' }, marca: 'Alfen', modello: 'Eve Double Pro-line', seriale: 'ACE0012345', firmware: '6.4.0',
    collegata: true, ultimoContattoAt: '2026-10-25T12:44:10Z', attiva: true, passwordImpostata: true,
    connettori: [
      {
        numero: 1, stato: 'Charging', codiceErrore: null,
        sessioneInCorso: { id: 8, transactionId: 1042, dipendente: 'Rossi Mario', inizioAt: '2026-10-25T07:42:00Z', energiaWh: 8120 },
      },
      { numero: 2, stato: 'Available', codiceErrore: null, sessioneInCorso: null },
    ],
  },
  {
    id: 2, societaId: 1, societaNome: 'Pianinvest', nome: 'Parcheggio ingresso 2', codiceOcpp: 'PIA-SEDE-02',
    sede: { id: 1, nome: 'Sede centrale' }, marca: 'ABB', modello: 'Terra AC W22', seriale: 'TACW22-4410', firmware: '1.8.21',
    collegata: true, ultimoContattoAt: '2026-10-25T12:41:55Z', attiva: true, passwordImpostata: true,
    connettori: [{ numero: 1, stato: 'Available', codiceErrore: null, sessioneInCorso: null }],
  },
  {
    id: 3, societaId: 2, societaNome: 'Gruppo Bonacina', nome: 'Magazzino nord', codiceOcpp: 'BON-MAG-01',
    sede: { id: 2, nome: 'Magazzino nord' }, marca: 'Alfen', modello: 'Eve Single Pro-line', seriale: 'ACE0019876', firmware: '6.3.2',
    collegata: false, ultimoContattoAt: '2026-10-23T16:02:00Z', attiva: true, passwordImpostata: true,
    connettori: [{ numero: 1, stato: 'Faulted', codiceErrore: 'GroundFailure', sessioneInCorso: null }],
  },
  {
    id: 4, societaId: 2, societaNome: 'Gruppo Bonacina', nome: 'Showroom', codiceOcpp: 'BON-SHW-01',
    sede: { id: 3, nome: 'Showroom' }, marca: null, modello: null, seriale: null, firmware: null,
    collegata: false, ultimoContattoAt: null, attiva: true, passwordImpostata: false,
    connettori: [],
  },
];

// Tariffe per società proprietaria delle colonnine (chiave: societaId), dalla più recente
export const TARIFFE: Record<number, Tariffa[]> = {
  1: [
    { id: 2, prezzoKwh: 0.28, validaDal: '2026-11-01', creatoDa: 'Paolo' },
    { id: 1, prezzoKwh: 0.25, validaDal: '2026-01-01', creatoDa: 'Paolo' },
  ],
  2: [
    { id: 3, prezzoKwh: 0.3, validaDal: '2026-01-01', creatoDa: 'Paolo' },
  ],
};

// Storico dei comandi per colonnina (chiave: id della colonnina), dal più recente
export const COMANDI: Record<number, Comando[]> = {
  1: [
    {
      id: 3, tipo: 'STOP_REMOTO', parametri: { transactionId: 1039 }, stato: 'ACCETTATO', risposta: { status: 'Accepted' }, messaggio: null,
      richiestoDa: 'Paolo', createdAt: '2026-10-23T11:09:57Z', esitoAt: '2026-10-23T11:10:00Z',
    },
    {
      id: 1, tipo: 'SBLOCCO', parametri: { connettore: 1 }, stato: 'ACCETTATO', risposta: { status: 'Unlocked' }, messaggio: null,
      richiestoDa: 'Paolo', createdAt: '2026-10-20T08:05:00Z', esitoAt: '2026-10-20T08:05:02Z',
    },
  ],
  3: [
    {
      id: 2, tipo: 'RESET', parametri: { tipo: 'Soft' }, stato: 'ERRORE', risposta: null, messaggio: 'Colonnina non collegata',
      richiestoDa: 'Paolo', createdAt: '2026-10-24T09:30:00Z', esitoAt: '2026-10-24T09:30:01Z',
    },
  ],
};

// Risposta simulata di "Leggi configurazione" (GetConfiguration di OCPP 1.6)
export const CONFIGURAZIONE_LETTA: Record<string, unknown> = {
  configurationKey: [
    { key: 'HeartbeatInterval', readonly: false, value: '300' },
    { key: 'MeterValueSampleInterval', readonly: false, value: '300' },
    { key: 'LocalAuthListEnabled', readonly: false, value: 'true' },
    { key: 'AuthorizeRemoteTxRequests', readonly: false, value: 'false' },
    { key: 'ConnectionTimeOut', readonly: false, value: '60' },
    { key: 'NumberOfConnectors', readonly: true, value: '2' },
  ],
};

export const DIPENDENTI: DipendenteOpzione[] = [
  { id: 1, nome: 'Mario', cognome: 'Rossi' },
  { id: 2, nome: 'Giulia', cognome: 'Bianchi' },
  { id: 3, nome: 'Luca', cognome: 'Verdi' },
  { id: 4, nome: 'Anna', cognome: 'Neri' },
  { id: 5, nome: 'Paolo', cognome: 'Conti' },
  { id: 6, nome: 'Sara', cognome: 'Ferri' },
];

export const BADGE: Badge[] = [
  {
    id: 1, codice: '04A1B2C3D4', descrizione: 'Badge presenze', dipendente: { id: 1, nome: 'Mario', cognome: 'Rossi' },
    validoDal: '2026-03-01', validoAl: null, attivo: true, ultimoUtilizzoAt: '2026-10-25T07:42:00Z',
  },
  {
    id: 2, codice: '04F9E8D7C6', descrizione: null, dipendente: { id: 2, nome: 'Giulia', cognome: 'Bianchi' },
    validoDal: '2026-03-01', validoAl: null, attivo: true, ultimoUtilizzoAt: '2026-10-24T08:05:00Z',
  },
  {
    id: 3, codice: '04112233AA', descrizione: 'Tessera smarrita il 15/10', dipendente: { id: 3, nome: 'Luca', cognome: 'Verdi' },
    validoDal: '2026-03-01', validoAl: '2026-10-15', attivo: false, ultimoUtilizzoAt: '2026-10-02T06:58:00Z',
  },
  {
    id: 4, codice: '0477889900', descrizione: null, dipendente: { id: 4, nome: 'Anna', cognome: 'Neri' },
    validoDal: '2026-04-15', validoAl: null, attivo: true, ultimoUtilizzoAt: '2026-10-21T13:20:00Z',
  },
];

export const BADGE_SCONOSCIUTI: BadgeSconosciuto[] = [
  {
    codiceBadge: '04DEADBEEF', tentativi: 3, ultimoTentativoAt: '2026-10-19T16:20:00Z',
    colonnina: { id: 1, nome: 'Parcheggio ingresso 1', sede: 'Sede centrale' },
  },
  {
    codiceBadge: '04C0FFEE11', tentativi: 1, ultimoTentativoAt: '2026-10-24T07:51:00Z',
    colonnina: { id: 2, nome: 'Parcheggio ingresso 2', sede: 'Sede centrale' },
  },
];

const C1 = { id: 1, nome: 'Parcheggio ingresso 1', societaNome: 'Pianinvest', sede: 'Sede centrale' };
const C2 = { id: 2, nome: 'Parcheggio ingresso 2', societaNome: 'Pianinvest', sede: 'Sede centrale' };
const C3 = { id: 3, nome: 'Magazzino nord', societaNome: 'Gruppo Bonacina', sede: 'Magazzino nord' };
const ROSSI = { id: 1, nome: 'Mario', cognome: 'Rossi', societaNome: 'Pianinvest' };
const BIANCHI = { id: 2, nome: 'Giulia', cognome: 'Bianchi', societaNome: 'Pianinvest' };
const VERDI = { id: 3, nome: 'Luca', cognome: 'Verdi', societaNome: 'Pianinvest' };
const NERI = { id: 4, nome: 'Anna', cognome: 'Neri', societaNome: 'Pianinvest' };
const GALLO = { id: 21, nome: 'Marco', cognome: 'Gallo', societaNome: 'Gruppo Bonacina' };
const OTTOBRE = { anno: 2026, mese: 10 };

export const SESSIONI: Sessione[] = [
  {
    id: 8, transactionId: 1042, colonnina: C1, connettore: 1, dipendente: ROSSI, codiceBadge: '04A1B2C3D4',
    inizioAt: '2026-10-25T07:42:00Z', fineAt: null, energiaWh: 8120, prezzoKwh: null, importo: null,
    stato: 'IN_CORSO', motivoAnomalia: null, tardiva: false, competenza: null,
  },
  {
    id: 7, transactionId: 1041, colonnina: C2, connettore: 1, dipendente: BIANCHI, codiceBadge: '04F9E8D7C6',
    inizioAt: '2026-10-24T08:05:00Z', fineAt: '2026-10-24T12:30:00Z', energiaWh: 18640, prezzoKwh: 0.25, importo: 4.66,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: false, competenza: OTTOBRE,
  },
  {
    id: 6, transactionId: 1039, colonnina: C1, connettore: 2, dipendente: ROSSI, codiceBadge: '04A1B2C3D4',
    inizioAt: '2026-10-23T07:55:00Z', fineAt: '2026-10-23T11:10:00Z', energiaWh: 22300, prezzoKwh: 0.25, importo: 5.58,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: false, competenza: OTTOBRE,
  },
  {
    id: 5, transactionId: 1037, colonnina: C1, connettore: 1, dipendente: NERI, codiceBadge: '0477889900',
    inizioAt: '2026-10-21T13:20:00Z', fineAt: '2026-10-21T17:45:00Z', energiaWh: 15210, prezzoKwh: 0.25, importo: 3.8,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: false, competenza: OTTOBRE,
  },
  {
    id: 4, transactionId: 1035, colonnina: C2, connettore: 1, dipendente: GALLO, codiceBadge: '04BB12CC34',
    inizioAt: '2026-10-20T07:30:00Z', fineAt: '2026-10-20T10:05:00Z', energiaWh: 9870, prezzoKwh: 0.25, importo: 2.47,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: false, competenza: OTTOBRE,
  },
  {
    id: 3, transactionId: 1033, colonnina: C1, connettore: 2, dipendente: null, codiceBadge: '04DEADBEEF',
    inizioAt: '2026-10-19T16:20:00Z', fineAt: '2026-10-19T16:41:00Z', energiaWh: 2140, prezzoKwh: null, importo: null,
    stato: 'ANOMALA',
    motivoAnomalia: "Badge non registrato: la colonnina ha avviato la ricarica con la sua lista locale non aggiornata",
    tardiva: false, competenza: null,
  },
  {
    id: 2, transactionId: 1030, colonnina: C3, connettore: 1, dipendente: BIANCHI, codiceBadge: '04F9E8D7C6',
    inizioAt: '2026-09-30T17:10:00Z', fineAt: '2026-09-30T19:10:00Z', energiaWh: 11200, prezzoKwh: 0.3, importo: 3.36,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: true, competenza: OTTOBRE,
  },
  {
    id: 1, transactionId: 1021, colonnina: C2, connettore: 1, dipendente: VERDI, codiceBadge: '04112233AA',
    inizioAt: '2026-10-02T06:58:00Z', fineAt: '2026-10-02T10:40:00Z', energiaWh: 20500, prezzoKwh: 0.25, importo: 5.13,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: false, competenza: OTTOBRE,
  },
  {
    id: 10, transactionId: 1005, colonnina: C2, connettore: 1, dipendente: NERI, codiceBadge: '0477889900',
    inizioAt: '2026-09-22T13:00:00Z', fineAt: '2026-09-22T16:20:00Z', energiaWh: 14600, prezzoKwh: 0.25, importo: 3.65,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: false, competenza: { anno: 2026, mese: 9 },
  },
  {
    id: 9, transactionId: 1001, colonnina: C1, connettore: 1, dipendente: ROSSI, codiceBadge: '04A1B2C3D4',
    inizioAt: '2026-09-15T07:30:00Z', fineAt: '2026-09-15T10:45:00Z', energiaWh: 19800, prezzoKwh: 0.25, importo: 4.95,
    stato: 'CONCLUSA', motivoAnomalia: null, tardiva: false, competenza: { anno: 2026, mese: 9 },
  },
];

// Riaddebiti per società del dipendente e mese (chiave: 'societaId-AAAA-MM').
// Settembre di Pianinvest è già chiuso: per questo la ricarica di Bianchi arrivata
// tardi è finita in ottobre. Gallo è di Gruppo Bonacina: la sua ricarica a una
// colonnina Pianinvest sta nel riaddebito di Gruppo Bonacina.
export const RIADDEBITI: Record<string, Riaddebito> = {
  '1-2026-10': {
    periodo: { stato: 'APERTO', chiusoAt: null, chiusoDa: null },
    righe: [
      { dipendente: { id: 2, nome: 'Giulia', cognome: 'Bianchi', codiceFiscale: 'BNCGLI85C41F205Z' }, sessioni: 2, energiaWh: 29840, importo: 8.02, tardive: 1 },
      { dipendente: { id: 4, nome: 'Anna', cognome: 'Neri', codiceFiscale: 'NRENNA90D52E507K' }, sessioni: 1, energiaWh: 15210, importo: 3.8, tardive: 0 },
      { dipendente: { id: 1, nome: 'Mario', cognome: 'Rossi', codiceFiscale: 'RSSMRA80A01E507X' }, sessioni: 1, energiaWh: 22300, importo: 5.58, tardive: 0 },
      { dipendente: { id: 3, nome: 'Luca', cognome: 'Verdi', codiceFiscale: 'VRDLCU78B12F205Q' }, sessioni: 1, energiaWh: 20500, importo: 5.13, tardive: 0 },
    ],
    totali: { sessioni: 5, energiaWh: 87850, importo: 22.53 },
    avvisi: { anomale: 1, inCorso: 1 },
  },
  '1-2026-09': {
    periodo: { stato: 'CHIUSO', chiusoAt: '2026-10-02T08:30:00Z', chiusoDa: 'Paolo' },
    righe: [
      { dipendente: { id: 4, nome: 'Anna', cognome: 'Neri', codiceFiscale: 'NRENNA90D52E507K' }, sessioni: 1, energiaWh: 14600, importo: 3.65, tardive: 0 },
      { dipendente: { id: 1, nome: 'Mario', cognome: 'Rossi', codiceFiscale: 'RSSMRA80A01E507X' }, sessioni: 1, energiaWh: 19800, importo: 4.95, tardive: 0 },
    ],
    totali: { sessioni: 2, energiaWh: 34400, importo: 8.6 },
    avvisi: { anomale: 0, inCorso: 0 },
  },
  '2-2026-10': {
    periodo: { stato: 'APERTO', chiusoAt: null, chiusoDa: null },
    righe: [
      { dipendente: { id: 21, nome: 'Marco', cognome: 'Gallo', codiceFiscale: 'GLLMRC82E15E507W' }, sessioni: 1, energiaWh: 9870, importo: 2.47, tardive: 0 },
    ],
    totali: { sessioni: 1, energiaWh: 9870, importo: 2.47 },
    avvisi: { anomale: 0, inCorso: 0 },
  },
};
